import AssetCollection from './asset'
import { MiniAppARSceneError } from './config/error'

export default class ARScene {
  private validAnchorOptions = ['random-distribution']
  private uniqueModelIds = new Set<string>()

  constructor(private assetCollection: AssetCollection) {}

  public configure(configuration: ARSceneConfiguration): ARSceneConfiguration {
    if (!this.validAnchorOptions.includes(configuration.anchor)) {
      throw new MiniAppARSceneError('INVALID_ANCHOR')
    }

    if (configuration.footer && configuration.footerInfluencer) {
      throw new MiniAppARSceneError('DUPLICATE_FOOTER')
    }

    const validModels = configuration.model.data.filter((modelItem) => {
      if (this.uniqueModelIds.has(modelItem.id)) {
        return false
      }

      const modelAssetExist = this.assetCollection
        .model3d()
        .exists(modelItem.model3d)

      const texturesExist = Object.entries(modelItem.texture2d).every(
        ([_, value]) => {
          return value ? this.assetCollection.texture2d().exists(value) : true
        }
      )

      const imageExist = modelItem.imageId
        ? this.assetCollection.image().exists(modelItem.imageId)
        : true

      if (modelAssetExist && imageExist && texturesExist) {
        this.uniqueModelIds.add(modelItem.id)
        return true
      }
      return false
    })

    const validSceneOverlay = configuration.overlays?.filter((overlay) => {
      return this.assetCollection.lottie().exists(overlay.id)
    })

    let footer: {
      footer?: Footer
      footerInfluencer?: FooterInfluencer
    } = {}
    if (configuration.footer) {
      const isValidFooter =
        configuration.footer?.background &&
        this.assetCollection.image().exists(configuration.footer?.background)

      if (isValidFooter) {
        footer.footer = configuration.footer
      }
    } else if (configuration.footerInfluencer) {
      const isValidFooterInfluencer =
        configuration.footerInfluencer &&
        Object.entries(configuration.footerInfluencer).every(([key, value]) => {
          if (key.endsWith('LottieId')) {
            return value ? this.assetCollection.lottie().exists(value) : true
          } else if (key.endsWith('ImageId')) {
            return value ? this.assetCollection.image().exists(value) : true
          } else {
            return false
          }
        })

      if (isValidFooterInfluencer) {
        footer.footerInfluencer = configuration.footerInfluencer
      }
    }

    const {
      model: { animation },
    } = configuration
    const validAnimation =
      animation &&
      animation.isEnabled &&
      animation.type &&
      animation.duration &&
      Array.isArray(animation.targetModelIds) &&
      animation.targetModelIds.length > 0 &&
      animation.winnerModelId &&
      this.uniqueModelIds.has(animation.winnerModelId) &&
      animation.targetModelIds.every((modelId) =>
        this.uniqueModelIds.has(modelId)
      )
        ? configuration.model.animation
        : undefined

    return {
      ...configuration,
      overlays: validSceneOverlay,
      model: {
        animation: validAnimation,
        data: validModels,
      },
      ...footer,
    }
  }

  public clearData() {
    return (this.uniqueModelIds = new Set<string>())
  }

  public modelExists(id: string) {
    return this.uniqueModelIds.has(id)
  }
}
